import { Camera, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";

interface SitePhotoCaptureProps {
  onCapture: (file: File, location: { lat: number; lng: number } | null) => void;
  onMultiCapture?: (files: File[], location: { lat: number; lng: number } | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
}

export function SitePhotoCapture({
  onCapture,
  onMultiCapture,
  multiple = false,
  disabled = false,
  label = "Add Photo",
}: SitePhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  function requestLocation(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 8000 },
      );
    });
  }

  async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    setLoading(true);
    const location = await requestLocation();
    setLoading(false);

    if (multiple && onMultiCapture) {
      onMultiCapture(files, location);
    } else {
      const file = files[0];
      if (file) onCapture(file, location);
    }

    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
        disabled={disabled || loading}
      />
      <Button
        type="button"
        variant="outline"
        className="min-h-[44px] gap-2"
        disabled={disabled || loading}
        onClick={() => inputRef.current?.click()}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        {label}
      </Button>
    </>
  );
}
